import { EventObject, fromCallback, fromPromise, setup } from 'xstate';

import { getStateHasPsychiatrists, patientMatchWithProvider, PublicAppointment } from '@uplift/api';
import { AgeGroupEnum, ProviderFilterTypeEnum } from '@uplift/types';

import { environment } from '../../../../../environments/environment';
import { BookingForEnum } from '../../types';
import {
  LoadActorReturnType, MatchingFlowTypeEnum, OnboardingMachineContext, OnboardingMachineEvent
} from '../types';
import { syncRouteInvocation } from './utils';

type GetPsychiatristsInStateOutput = {
  stateHasPsychiatrists: boolean;
};
type GetPsychiatristsInStateInput = {
  stateId: string;
};

type BookingBothBookPsychInput = {
  appointment: PublicAppointment;
  providerId: string;
  userId: string;
};

export const machineSetup = setup({
  types: {
    context: {} as OnboardingMachineContext,
    events: {} as OnboardingMachineEvent,
  },
  actors: {
    handleHistoryPop: fromCallback(() => {
      return;
    }),
    load: fromPromise(async (): Promise<LoadActorReturnType> => {
      return { matchingFlow: MatchingFlowTypeEnum.Marketplace };
    }),
    autoCalculateWho: fromPromise(async (): Promise<{ who: BookingForEnum; modalityId: string }> => {
      throw new Error('Not implemented');
    }),
    syncRouteActor: fromCallback<EventObject, ReturnType<typeof syncRouteInvocation>['input']>(() => {
      return;
    }),
    getPsychiatristsInState: fromPromise<GetPsychiatristsInStateOutput, GetPsychiatristsInStateInput>(async ({ input }) => {
      const data = await getStateHasPsychiatrists(input.stateId);

      // being explicit about the return type here to avoid any confusion
      return { stateHasPsychiatrists: data.data };
    }),
    bookingBothBookPsych: fromPromise<void, BookingBothBookPsychInput>(async ({ input }) => {
      const response = await patientMatchWithProvider({
        providerId: input.providerId,
        fromDatetime: input.appointment.fromDatetime,
      });

      if (!response?.data?.created || response?.data?.error) {
        const errorMessage = response?.data?.error || 'Unknown error';

        console.error('Error creating appointment', errorMessage);
        throw new Error(errorMessage);
      }
    })
  },
  actions: {
    redirectToLogin: () => window.location.href = environment.authUrl,
  },
  guards: {
    isBookingForMultiplePeople: ({ event, context }) => {
      let who = context.who;

      if (event.type === 'Who.Next') {
        who = event.data.who;
      }

      return [ BookingForEnum.MyselfAndFamily, BookingForEnum.MyselfAndPartner ].includes(who);
    },
    isBookingForSomeoneElse: ({ event, context }) => {
      let who = context.who;

      if (event.type === 'Who.Next') {
        who = event.data.who;
      }

      return who === BookingForEnum.SomeoneElse;
    },
    isTooYoung: ({ context, event }) => {
      let age = context.age;

      if (event.type === 'Age.Next') {
        age = event.data.age;
      }

      return age.ageGroup === AgeGroupEnum.children_or_teens;
    },
    showProviderIdentityFilter: ({ context, event }) => {
      let filters = context.showTheseProviderFilters;

      if (event.type === 'ProviderFilters.Next') {
        filters = event.data.providerFilters;
      }

      return filters.includes(ProviderFilterTypeEnum.Identity);
    },
    showProviderExperienceFilter: ({ context, event }) => {
      let filters = context.showTheseProviderFilters;

      if (event.type === 'ProviderFilters.Next') {
        filters = event.data.providerFilters;
      }

      return filters.includes(ProviderFilterTypeEnum.Experience);
    },
    isInsuranceMedicaid: () => {
      return false;
    },
    isDirect: ({ context }) => context.matchingFlow === MatchingFlowTypeEnum.Direct,
    isBetterHelpUser: ({ context }) => {
      return context.isBetterhelpUser;
    },
  }
});
